// A tiny head manager: pages register what their <head> should say and the
// newest, highest-priority registration wins field by field. The router
// registers a route-level default (canonical URL, noindex on private areas)
// at priority 0; pages register at priority 1 through usePageMeta. noindex
// is sticky: once any registration asks for it, the page carries it, so a
// page can never lift the router's noindex on a private area.

import {
  DEFAULT_DESCRIPTION,
  DEFAULT_IMAGE,
  SITE_NAME,
  formatTitle,
} from './site.js'

export const DEFAULTS = Object.freeze({
  title: SITE_NAME,
  description: DEFAULT_DESCRIPTION,
  image: DEFAULT_IMAGE,
  type: 'website',
  canonical: null,
  noindex: false,
  jsonLd: null,
})

const entries = []
let seq = 0

/** Merge registrations over the defaults (pure). */
export function resolveMeta(list = entries) {
  const ordered = [...list].sort(
    (a, b) => a.priority - b.priority || a.id - b.id
  )
  const merged = { ...DEFAULTS }
  for (const { meta } of ordered) {
    for (const [key, value] of Object.entries(meta || {})) {
      if (value === undefined || key === 'noindex') continue
      merged[key] = value
    }
  }
  merged.noindex = ordered.some(e => e.meta?.noindex)
  merged.title = formatTitle(merged.title)
  return merged
}

function upsert(doc, selector, create, set) {
  let el = doc.head.querySelector(selector)
  if (set === null) {
    if (el) el.remove()
    return
  }
  if (!el) {
    el = create()
    doc.head.appendChild(el)
  }
  set(el)
}

function setMeta(doc, attr, key, content) {
  upsert(
    doc,
    `meta[${attr}="${key}"]`,
    () => {
      const el = doc.createElement('meta')
      el.setAttribute(attr, key)
      return el
    },
    content ? el => el.setAttribute('content', content) : null
  )
}

/** Write resolved meta into document.head. */
export function writeHead(meta, doc = globalThis.document) {
  if (!doc?.head) return
  doc.title = meta.title
  setMeta(doc, 'name', 'description', meta.description)
  setMeta(doc, 'name', 'robots', meta.noindex ? 'noindex' : null)
  const canonical = meta.noindex ? null : meta.canonical
  upsert(
    doc,
    'link[rel="canonical"]',
    () => {
      const el = doc.createElement('link')
      el.setAttribute('rel', 'canonical')
      return el
    },
    canonical ? el => el.setAttribute('href', canonical) : null
  )
  setMeta(doc, 'property', 'og:type', meta.type || 'website')
  setMeta(doc, 'property', 'og:title', meta.title)
  setMeta(doc, 'property', 'og:description', meta.description)
  setMeta(doc, 'property', 'og:url', meta.canonical)
  setMeta(doc, 'property', 'og:image', meta.image)
  setMeta(
    doc,
    'name',
    'twitter:card',
    meta.image && meta.image !== DEFAULT_IMAGE
      ? 'summary_large_image'
      : 'summary'
  )
  setMeta(doc, 'name', 'twitter:title', meta.title)
  setMeta(doc, 'name', 'twitter:description', meta.description)
  setMeta(doc, 'name', 'twitter:image', meta.image)
  upsert(
    doc,
    'script[data-head="page"]',
    () => {
      const el = doc.createElement('script')
      el.setAttribute('type', 'application/ld+json')
      el.setAttribute('data-head', 'page')
      return el
    },
    meta.jsonLd
      ? el => {
          el.textContent = JSON.stringify(meta.jsonLd)
        }
      : null
  )
}

function apply() {
  writeHead(resolveMeta(entries))
}

/**
 * Register head meta; returns the function that removes it again.
 * priority 0 = route defaults, 1 = the page.
 */
export function pushMeta(meta, priority = 1) {
  const entry = { id: ++seq, priority, meta }
  entries.push(entry)
  apply()
  return () => {
    const i = entries.indexOf(entry)
    if (i !== -1) entries.splice(i, 1)
    apply()
  }
}

/** Test helper: forget every registration. */
export function resetHead() {
  entries.length = 0
}
